import { Badge, Flex, Text, Tooltip } from "@chakra-ui/react";
import StatusBadge from "components/badge/StatusBadge";
import { formatTime } from "constants/commonFunctions";
import { formatDate } from "constants/commonFunctions";
import { formatDateToDDMONTHNAMEYYYY } from "utils/common";

const ROLE_COLOR_MAP = {
  owner: "green.600",
  landlord: "green.600",
  pg_owner: "green.500",
  agent: "red.600",
  agent_dealer: "red.600",
  dealer: "red.600",
  builder: "orange.600",
  buyer: "purple.600",
  tenant: "teal.600",
  pg_seeker: "pink.600",
  coliving: "yellow.700",
  co_living: "yellow.700",
};
const roleColor = (role) => ROLE_COLOR_MAP[(role || "").toLowerCase()] || "gray.600";

const PLAN_BADGE_SCHEME = {
  "freemium": "gray",
  "pg seeker": "pink",
  "agent": "red",
  "seller": "orange",
  "landlord": "green",
  "pg owner": "teal",
  "buyer": "purple",
  "tenant": "cyan",
  "co-living": "yellow",
  "coliving": "yellow",
};
const planScheme = (planName) => {
  const lower = (planName || "").toLowerCase().trim();
  if (!lower) return "purple";
  if (PLAN_BADGE_SCHEME[lower]) return PLAN_BADGE_SCHEME[lower];
  const family = lower.replace(/\s*(pro plus|pro)\s*$/, "").trim();
  return PLAN_BADGE_SCHEME[family] || "purple";
};

export const directLeadColumn = [
  {
    Header: "ID",
    accessor: "_id",
    Cell: ({ value }) => <Text>{value}</Text>,
  },
  {
    Header: "OWNER",
    accessor: "user_data",
    Cell: ({ value }) => {
      const name = `${value?.first_name || ""} ${value?.last_name || ""}`.trim();
      const phone = value?.phone;
      const primaryRole = value?.classified_primary_role || value?.role;
      const planName = value?.plan_name;
      const platform = value?.platform;
      const limitExhausted = value?.contact_view_limit_exhausted;
      const lastActivity = value?.last_activity_at
        ? `${formatDate(value.last_activity_at)}, ${formatTime(value.last_activity_at)}`
        : null;
      if (!name && !phone) return <Text>-</Text>;
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          bg={limitExhausted ? "#d4edda" : "transparent"}
          borderRadius="md"
          p={limitExhausted ? 2 : 0}
        >
          <Text as="span">
            {name || "-"}
            {(primaryRole || platform) && (
              <Text as="span">
                {" "}(
                {primaryRole && (
                  <Text as="span" color={roleColor(primaryRole)} fontWeight="600">
                    {primaryRole}
                  </Text>
                )}
                {primaryRole && platform && <Text as="span">, </Text>}
                {platform && (
                  <Text as="span" color="gray.600">
                    {platform}
                  </Text>
                )}
                )
              </Text>
            )}
          </Text>
          {phone && (
            <a href={`/#/admin/users?search_text=${phone}`}>
              <Text fontSize="xs" color="blue.500">{phone}</Text>
            </a>
          )}
          {planName && (
            <Badge
              colorScheme={planScheme(value?.plan_name)}
              variant="solid"
              borderRadius="md"
              fontSize="0.65rem"
              mt={1}
              px={2}
            >
              {planName}
            </Badge>
          )}
          {lastActivity && (
            <Text fontSize="0.65rem" color="gray.600" mt={1}>
              Last activity: {lastActivity}
            </Text>
          )}
        </Flex>
      );
    },
  },
  {
    Header: "LEAD",
    accessor: "lead_data",
    Cell: ({ value }) => {
      const name = `${value?.first_name || ""} ${value?.last_name || ""}`.trim();
      const phone = value?.phone;
      const primaryRole = value?.classified_primary_role || value?.role;
      const planName = value?.plan_name;
      const platform = value?.platform;
      const limitExhausted = value?.contact_view_limit_exhausted;
      const lastActivity = value?.last_activity_at
        ? `${formatDate(value.last_activity_at)}, ${formatTime(value.last_activity_at)}`
        : null;
      if (!name && !phone) return <Text>-</Text>;
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          bg={limitExhausted ? "#d4edda" : "transparent"}
          borderRadius="md"
          p={limitExhausted ? 2 : 0}
        >
          <Text as="span">
            {name || "-"}
            {(primaryRole || platform) && (
              <Text as="span">
                {" "}(
                {primaryRole && (
                  <Text as="span" color={roleColor(primaryRole)} fontWeight="600">
                    {primaryRole}
                  </Text>
                )}
                {primaryRole && platform && <Text as="span">, </Text>}
                {platform && (
                  <Text as="span" color="gray.600">
                    {platform}
                  </Text>
                )}
                )
              </Text>
            )}
          </Text>
          {phone && (
            <a href={`/#/admin/users?search_text=${phone}`}>
              <Text fontSize="xs" color="blue.500">{phone}</Text>
            </a>
          )}
          {planName && (
            <Badge
              colorScheme={planScheme(value?.plan_name)}
              variant="solid"
              borderRadius="md"
              fontSize="0.65rem"
              mt={1}
              px={2}
            >
              {planName}
            </Badge>
          )}
          {lastActivity && (
            <Text fontSize="0.65rem" color="gray.600" mt={1}>
              Last activity: {lastActivity}
            </Text>
          )}
        </Flex>
      );
    },
  },
  {
    Header: "VIEWED",
    accessor: "viewed",
    Cell: ({ value }) => (
      <>{value ? <StatusBadge bgColor="green" label="Viewed" /> : "-"}</>
    ),
  },
  {
    Header: "AD TYPE",
    accessor: "requirement_data",
    Cell: ({ value }) => {
      const { ad_type, shared_accommodation_interest } = value || {};
      let text = ad_type;
      if (shared_accommodation_interest) text += ` (CO-LIVING)`;
      return (
        <Flex gap={1} align="center" justify={"center"}>
          <a target="_blank" href={`/#/admin/user-requirements?requirement_id=${value._id}`}>
            {text || "-"}
          </a>
        </Flex>
      );
    },
  },
  {
    Header: "PROPERTY TYPE",
    accessor: "requirement_data.property_type",
    Cell: ({ value }) => <Text>{value || "-"}</Text>,
  },
  {
    Header: "LOCATION",
    accessor: "requirement_data.location",
    Cell: ({ value }) => {
      const { locality, city } = value || {};
      let text = `${locality ? `${locality}, ` : ""}${city}`;
      return <Text>{text || "-"}</Text>;
    },
  },
  {
    Header: "SENT ON",
    accessor: "created_at",
    Cell: ({ value }) => (
      <Text>
        {formatDateToDDMONTHNAMEYYYY(value)} {formatTime(value)}
      </Text>
    ),
  },
  {
    Header: "AI COMMENT",
    accessor: "lead_data.classified_comment",
    Cell: ({ value = "" }) => (
      <Flex align="center" justify="center">
        {value?.length > 30 ? (
          <Tooltip label={value} aria-label="ai comment">
            <Text>{`${value.slice(0, 30)}...`}</Text>
          </Tooltip>
        ) : (
          <Text>{value || "-"}</Text>
        )}
      </Flex>
    ),
  },
  {
    Header: "COMMENT",
    accessor: "comment",
    Cell: ({ value = "" }) => (
      <Flex align="center" justify="center">
        {value?.length > 20 ? (
          <Tooltip label={value} aria-label="comment">
            <Text>{`${value.slice(0, 20)}...`}</Text>
          </Tooltip>
        ) : (
          <Text>{value || "-"}</Text>
        )}
      </Flex>
    ),
  },
  {
    Header: "COMMENT INFO",
    accessor: "commented_by",
    Cell: ({ value, row }) => {
      const commentedAt = row?.original?.commented_at;
      const commentUpdatedAt = row?.original?.comment_updated_at;
      const createdStr = commentedAt
        ? `${formatDate(commentedAt)}, ${formatTime(commentedAt)}`
        : null;
      const updatedStr = commentUpdatedAt
        ? `${formatDate(commentUpdatedAt)}, ${formatTime(commentUpdatedAt)}`
        : null;
      if (!value && !createdStr && !updatedStr) return <Text>-</Text>;
      return (
        <Flex direction="column" align="center" justify="center">
          {value && <Text fontWeight="600">{value}</Text>}
          {createdStr && (
            <Text fontSize="0.65rem" color="gray.600">
              Created: {createdStr}
            </Text>
          )}
          {updatedStr && updatedStr !== createdStr && (
            <Text fontSize="0.65rem" color="gray.600">
              Updated: {updatedStr}
            </Text>
          )}
        </Flex>
      );
    },
  },
];
